import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Truck, CheckCircle2, AlertTriangle, Plus, Search, Plane, Ship, Users, Settings, FileSpreadsheet, QrCode } from "lucide-react";
import { api, API, ALL_STATUSES, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const StatCard = ({ label, value, icon: Icon, accent, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className={`h-5 w-5 ${accent}`} />
    </div>
    <p className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900">{value}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [late, setLate] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    api.get("/shipments/late").then(({ data }) => setLate(data)).catch(() => setLate([]));
    api.get("/stats/monthly").then(({ data }) => setMonthly(data)).catch(() => setMonthly([]));
  }, []);

  const exportUrl = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    if (type !== "all") params.set("shipping_type", type);
    if (destination.trim()) params.set("destination", destination.trim());
    return `${API}/shipments/export?${params.toString()}`;
  };

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status !== "all") params.status = status;
    if (type !== "all") params.shipping_type = type;
    if (destination.trim()) params.destination = destination.trim();
    const [s, list] = await Promise.all([api.get("/stats"), api.get("/shipments", { params })]);
    setStats(s.data);
    setShipments(list.data);
    setLoading(false);
  }, [q, status, type, destination]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Back-office</p>
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              Tableau de bord des livraisons
            </h1>
          </div>
          <Button
            className="gap-2 rounded-full bg-[#2563EB] px-6 font-semibold text-white hover:bg-[#1D4ED8]"
            onClick={() => navigate("/admin/nouvelle-livraison")}
            data-testid="new-shipment-button"
          >
            <Plus className="h-4 w-4" /> Nouvelle livraison
          </Button>
          <Button
            variant="outline"
            className="gap-2 rounded-full border-slate-300 px-6 font-semibold text-[#1E3A8A]"
            onClick={() => navigate("/admin/clients")}
            data-testid="clients-nav-button"
          >
            <Users className="h-4 w-4" /> Clients
          </Button>
          <Button
            variant="outline"
            className="gap-2 rounded-full border-slate-300 px-6 font-semibold text-[#1E3A8A]"
            onClick={() => navigate("/admin/parametres")}
            data-testid="settings-nav-button"
          >
            <Settings className="h-4 w-4" /> Paramètres
          </Button>
          <Button
            variant="outline"
            className="gap-2 rounded-full border-slate-300 px-6 font-semibold text-[#1E3A8A]"
            onClick={() => window.open(exportUrl(), "_blank")}
            data-testid="export-csv-button"
          >
            <FileSpreadsheet className="h-4 w-4" /> Export Excel
          </Button>
          <Button
            variant="outline"
            disabled={selected.length === 0}
            className="gap-2 rounded-full border-slate-300 px-6 font-semibold text-[#1E3A8A]"
            onClick={() => navigate(`/admin/etiquettes?ids=${selected.join(",")}`)}
            data-testid="batch-labels-button"
          >
            <QrCode className="h-4 w-4" /> Étiquettes ({selected.length})
          </Button>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Colis en cours" value={stats?.in_progress ?? "—"} icon={Truck}
                    accent="text-[#2563EB]" testId="stat-in-progress" />
          <StatCard label="Livrés" value={stats?.delivered ?? "—"} icon={CheckCircle2}
                    accent="text-emerald-600" testId="stat-delivered" />
          <StatCard label="En retard" value={stats?.late ?? "—"} icon={AlertTriangle}
                    accent="text-amber-500" testId="stat-late" />
          <StatCard label="Total livraisons" value={stats?.total ?? "—"} icon={Package}
                    accent="text-[#1E3A8A]" testId="stat-total" />
        </div>

        {late.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="late-alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-amber-900">
                  {late.length} colis {late.length > 1 ? "ont" : "a"} dépassé la date de livraison estimée
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {late.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/admin/livraison/${s.id}`)}
                      className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-left transition-colors hover:bg-amber-100"
                      data-testid={`late-item-${s.tracking_number}`}
                    >
                      <span className="font-mono text-xs font-bold text-[#1E3A8A]">
                        {s.tracking_number}
                      </span>
                      <span className="ml-2 text-xs text-amber-800">
                        prévu le {formatDate(s.estimated_delivery)} · {s.status}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {monthly.length > 0 && (
          <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="monthly-stats">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                Colis par mois
              </p>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="aerien" name="Aérien" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="maritime" name="Maritime" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                Poids expédié par mois (kg)
              </p>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="poids_aerien" name="Aérien" fill="#60A5FA" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="poids_maritime" name="Maritime" fill="#1E40AF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid gap-3 border-b border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Numéro, nom, téléphone"
                className="h-11 pl-9"
                data-testid="admin-search-input"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-11" data-testid="filter-status-select">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-11" data-testid="filter-type-select">
                <SelectValue placeholder="Type d'envoi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="aerien">Aérien</SelectItem>
                <SelectItem value="maritime">Maritime</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Destination (ex. Abidjan)"
              className="h-11"
              data-testid="filter-destination-input"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="bg-slate-50 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer accent-[#2563EB]"
                      checked={shipments.length > 0 && selected.length === shipments.length}
                      onChange={(e) => setSelected(e.target.checked ? shipments.map((s) => s.id) : [])}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-4 py-3">Numéro</th>
                  <th className="px-4 py-3">Destinataire</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3">Livraison est.</th>
                  <th className="px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Chargement…</td></tr>
                )}
                {!loading && shipments.length === 0 && (
                  <tr data-testid="empty-shipments-row">
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      Aucune livraison ne correspond aux filtres.
                    </td>
                  </tr>
                )}
                {!loading && shipments.map((s) => (
                  <tr
                    key={s.id}
                    className="cursor-pointer transition-colors hover:bg-blue-50/60"
                    data-testid={`shipment-row-${s.tracking_number}`}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-[#2563EB]"
                        checked={selected.includes(s.id)}
                        onChange={() => toggle(s.id)}
                        data-testid={`select-${s.tracking_number}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono font-bold text-[#1E3A8A]"
                        onClick={() => navigate(`/admin/livraison/${s.id}`)}>{s.tracking_number}</td>
                    <td className="px-4 py-3" onClick={() => navigate(`/admin/livraison/${s.id}`)}>
                      <p className="font-semibold text-slate-800">{s.recipient.name}</p>
                      <p className="text-xs text-slate-500">{s.recipient.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        {s.shipping_type === "aerien"
                          ? <Plane className="h-4 w-4 text-[#2563EB]" />
                          : <Ship className="h-4 w-4 text-[#2563EB]" />}
                        {s.shipping_type === "aerien" ? "Aérien" : "Maritime"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{s.destination_city}</td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(s.estimated_delivery)}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
